import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Users as UsersIcon,
    UserPlus,
    Trash2,
    Loader2,
    KeyRound,
    ShieldCheck,
    ShieldHalf,
    Mail,
    Sparkles,
    X,
    Save,
    Building2,
    Check,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    getCompanyMe,
    listCompanyUsers,
    createCompanyUser,
    updateCompanyUser,
    deleteCompanyUser,
    resetCompanyUserPassword,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MODULE_LABELS = {
    licitaciones: "Licitaciones",
    "buscar-plcsp": "Buscador PLCSP",
    "cumplimentar-anexos": "Cumplimentar anexos",
    solvencia: "Solvencia",
    legal: "Auxiliar Legal",
    "mi-documentacion": "Mi documentación",
    calendario: "Calendario",
    comunicaciones: "Comunicaciones",
    alertas: "Alertas",
    competidores: "Competidores",
    analiticas: "Analíticas",
    "perfil-empresa": "Perfil de empresa",
    usuarios: "Usuarios (gestión)",
    seguridad: "Seguridad",
    auditoria: "Auditoría",
};

function genPassword() {
    const A = "ABCDEFGHJKMNPQRSTUVWXYZ";
    const a = "abcdefghijkmnpqrstuvwxyz";
    const d = "23456789";
    const s = "!@#$%&";
    const pick = (set, n) => Array.from({ length: n }, () => set[Math.floor(Math.random() * set.length)]).join("");
    return (pick(A, 2) + pick(a, 6) + pick(d, 3) + pick(s, 2))
        .split("")
        .sort(() => Math.random() - 0.5)
        .join("");
}

export default function Users() {
    const { user: me } = useAuth();
    const [company, setCompany] = useState(null);
    const [modules, setModules] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} (new) | user object (edit)

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [cm, list] = await Promise.all([getCompanyMe(), listCompanyUsers()]);
            setCompany(cm.company);
            setModules(cm.modules || []);
            setUsers(list.users || []);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo cargar la gestión de usuarios"
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleDelete = async (u) => {
        if (!window.confirm(`¿Eliminar a ${u.name} (${u.email})?`)) return;
        try {
            await deleteCompanyUser(u.user_id);
            toast.success("Usuario eliminado");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al eliminar");
        }
    };

    const handleResetPassword = async (u) => {
        const newP = window.prompt(
            `Nueva contraseña para ${u.email} (mín. 10 caracteres, mayús+minús+nº+símbolo):`,
            genPassword()
        );
        if (!newP) return;
        try {
            await resetCompanyUserPassword(u.user_id, newP);
            toast.success(`Contraseña actualizada · cópiala: ${newP}`, { duration: 12000 });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al resetear contraseña");
        }
    };

    const stats = useMemo(() => {
        const admins = users.filter((u) => u.company_role === "admin").length;
        const members = users.length - admins;
        return { total: users.length, admins, members };
    }, [users]);

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-8" data-testid="users-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-violet-600 mb-2 flex items-center gap-1.5">
                            <Building2 size={12} />
                            {company?.name || "Tu empresa"}
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Usuarios
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium max-w-2xl">
                            Como administrador puedes crear cuentas para tu equipo y elegir
                            qué módulos puede ver cada persona. Las licitaciones, solvencia,
                            comunicaciones y documentos se comparten en tu empresa.
                        </p>
                    </div>
                    <Button
                        onClick={() => setEditing({})}
                        className="rounded-full h-11 px-5 bg-ink-950 hover:bg-ink-900 text-white font-bold gap-1.5 shadow-pop"
                        data-testid="users-new"
                    >
                        <UserPlus size={15} />
                        Invitar usuario
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                    {[
                        { label: "Usuarios", value: stats.total, icon: UsersIcon, color: "from-slate-700 to-slate-900" },
                        { label: "Administradores", value: stats.admins, icon: ShieldCheck, color: "from-violet-600 to-fuchsia-700" },
                        { label: "Miembros", value: stats.members, icon: ShieldHalf, color: "from-blue-600 to-indigo-700" },
                    ].map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${s.color} text-white shadow-pop`}
                            >
                                <div className="absolute -top-3 -right-3 opacity-15">
                                    <Icon size={64} />
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-80 mb-1">
                                    {s.label}
                                </div>
                                <div className="font-display font-black tabular-nums leading-none text-4xl">
                                    {s.value}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* List */}
                {loading ? (
                    <div className="bg-white border border-ink-100 rounded-2xl p-16 text-center">
                        <Loader2 className="mx-auto text-brand-600 animate-spin mb-3" size={28} />
                        <div className="text-sm font-bold text-ink-500">Cargando…</div>
                    </div>
                ) : (
                    <div className="bg-white border border-ink-100 rounded-2xl shadow-pop overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-ink-50 border-b border-ink-100">
                                <tr className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                                    <th className="text-left px-4 py-3">Usuario</th>
                                    <th className="text-left px-4 py-3 w-24">Rol</th>
                                    <th className="text-left px-4 py-3">Permisos</th>
                                    <th className="text-left px-4 py-3 w-32">Creado</th>
                                    <th className="px-4 py-3 w-28"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => {
                                    const isAdmin = u.company_role === "admin";
                                    const isMe = u.user_id === me?.user_id;
                                    const perms = u.permissions || [];
                                    const allModules = perms.includes("*") || isAdmin;
                                    return (
                                        <tr
                                            key={u.user_id}
                                            className="border-b border-ink-50 hover:bg-ink-50/50"
                                            data-testid={`users-row-${u.user_id}`}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="font-bold text-ink-950 flex items-center gap-1.5">
                                                    {u.name}
                                                    {isMe && (
                                                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            Tú
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] font-mono text-ink-500 flex items-center gap-1 mt-0.5">
                                                    <Mail size={10} />
                                                    {u.email}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
                                                        isAdmin
                                                            ? "bg-violet-50 text-violet-700 border-violet-200"
                                                            : "bg-slate-50 text-slate-700 border-slate-200"
                                                    }`}
                                                >
                                                    {isAdmin ? <ShieldCheck size={10} /> : <ShieldHalf size={10} />}
                                                    {isAdmin ? "Admin" : "Miembro"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {allModules ? (
                                                    <span className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1">
                                                        <Check size={12} />
                                                        Acceso total
                                                    </span>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1">
                                                        {perms.slice(0, 4).map((p) => (
                                                            <span
                                                                key={p}
                                                                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200"
                                                            >
                                                                {MODULE_LABELS[p] || p}
                                                            </span>
                                                        ))}
                                                        {perms.length > 4 && (
                                                            <span className="text-[10px] font-bold text-ink-500">
                                                                +{perms.length - 4} más
                                                            </span>
                                                        )}
                                                        {perms.length === 0 && (
                                                            <span className="text-xs text-ink-400 italic">
                                                                Sin módulos asignados
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs text-ink-500">
                                                {u.created_at?.slice(0, 10)}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex gap-1 justify-end">
                                                    <button
                                                        onClick={() => setEditing(u)}
                                                        className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600"
                                                        title="Editar permisos"
                                                        data-testid={`users-edit-${u.user_id}`}
                                                    >
                                                        <Sparkles size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleResetPassword(u)}
                                                        className="p-1.5 rounded-md hover:bg-amber-50 text-amber-600"
                                                        title="Resetear contraseña"
                                                    >
                                                        <KeyRound size={13} />
                                                    </button>
                                                    {!isMe && (
                                                        <button
                                                            onClick={() => handleDelete(u)}
                                                            className="p-1.5 rounded-md hover:bg-red-50 text-red-500"
                                                            title="Eliminar"
                                                            data-testid={`users-del-${u.user_id}`}
                                                        >
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <AnimatePresence>
                    {editing && (
                        <UserEditor
                            user={editing}
                            modules={modules}
                            onClose={() => setEditing(null)}
                            onSaved={async () => {
                                setEditing(null);
                                await load();
                            }}
                        />
                    )}
                </AnimatePresence>
            </div>
        </Layout>
    );
}

function UserEditor({ user, modules, onClose, onSaved }) {
    const isEdit = Boolean(user?.user_id);
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [password, setPassword] = useState(isEdit ? "" : genPassword());
    const [role, setRole] = useState(user?.company_role || "member");
    const [perms, setPerms] = useState(() => {
        const p = user?.permissions || [];
        if (p.includes("*")) return new Set(modules);
        return new Set(p);
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        // When role flips to admin, mark all
        if (role === "admin") setPerms(new Set(modules));
    }, [role, modules]);

    const togglePerm = (m) => {
        const next = new Set(perms);
        next.has(m) ? next.delete(m) : next.add(m);
        setPerms(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            const permsArr =
                role === "admin"
                    ? ["*"]
                    : Array.from(perms);
            if (isEdit) {
                await updateCompanyUser(user.user_id, {
                    name: name.trim(),
                    company_role: role,
                    permissions: permsArr,
                });
                toast.success("Usuario actualizado");
            } else {
                await createCompanyUser({
                    email: email.trim().toLowerCase(),
                    name: name.trim(),
                    password,
                    company_role: role,
                    permissions: permsArr,
                });
                toast.success(`Usuario creado. Contraseña inicial: ${password}`, {
                    duration: 15000,
                });
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
                data-testid="users-editor"
            >
                <div className="flex items-center justify-between p-5 border-b border-ink-100 sticky top-0 bg-white z-10">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-600">
                            {isEdit ? "Editar usuario" : "Invitar nuevo usuario"}
                        </div>
                        <div className="font-display text-xl font-black">
                            {isEdit ? user.name : "Nuevo miembro de la empresa"}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-ink-50">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 space-y-4 overflow-auto flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                                Nombre
                            </Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid="users-editor-name"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                                Email
                            </Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isEdit}
                                data-testid="users-editor-email"
                            />
                        </div>
                    </div>
                    {!isEdit && (
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                                Contraseña inicial
                            </Label>
                            <div className="flex gap-2">
                                <Input
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="font-mono"
                                />
                                <Button
                                    type="button"
                                    onClick={() => setPassword(genPassword())}
                                    variant="outline"
                                    className="rounded-md h-10 px-3 font-bold"
                                >
                                    Generar
                                </Button>
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                            Rol
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                ["admin", "Administrador", "Acceso total · puede gestionar usuarios", ShieldCheck, "from-violet-500 to-fuchsia-600"],
                                ["member", "Miembro", "Acceso restringido a los módulos elegidos abajo", ShieldHalf, "from-blue-500 to-indigo-600"],
                            ].map(([k, l, d, Icon, grad]) => (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => setRole(k)}
                                    className={`p-3 rounded-xl border-2 text-left transition-all ${role === k ? `border-ink-950 bg-gradient-to-br ${grad} text-white` : "border-ink-100 bg-white hover:border-ink-300"}`}
                                    data-testid={`users-editor-role-${k}`}
                                >
                                    <Icon size={18} className="mb-1" />
                                    <div className="font-display text-base font-black">{l}</div>
                                    <div className={`text-[11px] font-medium leading-snug ${role === k ? "opacity-90" : "text-ink-500"}`}>
                                        {d}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    {role === "member" && (
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-2">
                                Módulos a los que puede acceder
                            </Label>
                            <div className="grid grid-cols-2 gap-1.5 p-3 bg-ink-50 rounded-xl">
                                {modules.map((m) => (
                                    <label
                                        key={m}
                                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-white cursor-pointer transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={perms.has(m)}
                                            onChange={() => togglePerm(m)}
                                            className="w-4 h-4 accent-brand-600"
                                            data-testid={`users-editor-perm-${m}`}
                                        />
                                        <span className="text-sm font-medium text-ink-700">
                                            {MODULE_LABELS[m] || m}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-ink-100 sticky bottom-0 bg-white">
                    <Button variant="outline" onClick={onClose} className="rounded-full h-10 px-5 font-bold">
                        Cancelar
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-full h-10 px-5 bg-ink-950 hover:bg-ink-900 text-white font-bold gap-1.5"
                        data-testid="users-editor-save"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {isEdit ? "Guardar cambios" : "Invitar"}
                    </Button>
                </div>
            </motion.div>
        </motion.div>
    );
}
